import { DataTable, MainButton, STATUSPAGE } from '@repo/ui';
import {
  GetAllNomenclaturesFromEntityUseCaseInterface,
  UpdateNomenclaturesFromEntityUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { makeNomenclaturesFromEntityListColumns } from 'presentation/app/modules/Entities/common/columns/entity-settings/nomenclatures-from-entity-list-columns';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearNomenclaturesListData,
  loadNomenclaturesListData,
} from 'presentation/config/store/entities/entities-slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Card, CardBody, Form } from 'reactstrap';
import * as Yup from 'yup';


interface IProps {
  entityId: string;
}

export const NomenclaturesTabContent = ({ entityId }: IProps) => {
  // Hooks
  const { entitiesService } = useService();
  const dispatch = useDispatch();

  // States
  const { nomenclaturesFromEntityListData } = useAppSelector(
    (state) => state.Entities,
  );
  const [
    requestGetALlNomenclaturesFromEntityStatus,
    setRequestGetALlNomenclaturesFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);
  const [
    requestUpdateNomenclaturesFromEntityStatus,
    setRequestUpdateNomenclaturesFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);

  // Methods
  const requestGetALlNomenclaturesFromEntity = useCallback(
    async (params: GetAllNomenclaturesFromEntityUseCaseInterface.Params) => {
      setRequestGetALlNomenclaturesFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getAllNomenclaturesFromEntity(params)
        .then((res) => {
          dispatch(loadNomenclaturesListData(res));
        })
        .catch((err) => {
          dispatch(clearNomenclaturesListData());
          notifyError(err.message);
        })
        .finally(() => {
          setRequestGetALlNomenclaturesFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateNomenclaturesFromEntity = useCallback(
    async (params: UpdateNomenclaturesFromEntityUseCaseInterface.Params) => {
      setRequestUpdateNomenclaturesFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .updateNomenclaturesFromEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetALlNomenclaturesFromEntity({ entityId: params.entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestUpdateNomenclaturesFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      course: {
        singular:
          nomenclaturesFromEntityListData[0]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[0]?.pluralInput?.value || '',
      },
      semester: {
        singular:
          nomenclaturesFromEntityListData[1]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[1]?.pluralInput?.value || '',
      },
      group: {
        singular:
          nomenclaturesFromEntityListData[2]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[2]?.pluralInput?.value || '',
      },
      curricularUnit: {
        singular:
          nomenclaturesFromEntityListData[3]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[3]?.pluralInput?.value || '',
      },
      internshipLocation: {
        singular:
          nomenclaturesFromEntityListData[4]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[4]?.pluralInput?.value || '',
      },
      rector: {
        singular:
          nomenclaturesFromEntityListData[5]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[5]?.pluralInput?.value || '',
      },
      coordinator: {
        singular:
          nomenclaturesFromEntityListData[6]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[6]?.pluralInput?.value || '',
      },
      teacher: {
        singular:
          nomenclaturesFromEntityListData[7]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[7]?.pluralInput?.value || '',
      },
      preceptor: {
        singular:
          nomenclaturesFromEntityListData[8]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[8]?.pluralInput?.value || '',
      },
      student: {
        singular:
          nomenclaturesFromEntityListData[9]?.singularInput?.value || '',
        plural: nomenclaturesFromEntityListData[9]?.pluralInput?.value || '',
      },
    },
    validationSchema: Yup.object({
      rows: Yup.array().of(
        Yup.object({
          singular: Yup.string().notRequired(),
          plural: Yup.string().notRequired(),
        }),
      ),
    }),
    onSubmit: (values) => {
      const valuesToSubmit: UpdateNomenclaturesFromEntityUseCaseInterface.Params =
        {
          entityId,
          data: nomenclaturesFromEntityListData.map((registry) => {
            return {
              id: registry.id || null,
              key: registry.key,
              single: values[registry.key]?.singular || '',
              plural: values[registry.key]?.plural || '',
            };
          }),
        };

      requestUpdateNomenclaturesFromEntity(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetALlNomenclaturesFromEntity({ entityId });
  }, []);

  return (
    <Card className="mt-5">
      <CardBody>
        <Alert color="dark">
          <i className="ri-information-line me-2 fs-16"></i>
          Aqui você pode personalizar nomenclaturas do sistema de acordo com a
          organização, melhorando a compreensão e eficiência do usuário. Caso
          não queira personalizar, deixe os campos em branco.
        </Alert>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="d-flex flex-column gap-3">
            <DataTable
              data={nomenclaturesFromEntityListData}
              keyField="key"
              actions={[]}
              columns={makeNomenclaturesFromEntityListColumns({
                validation,
                disabled:
                  requestGetALlNomenclaturesFromEntityStatus ===
                  STATUSPAGE.LOADING,
              })}
              loading={
                requestGetALlNomenclaturesFromEntityStatus ===
                STATUSPAGE.LOADING
              }
              canSearch={false}
              pagination={false}
            />
            {nomenclaturesFromEntityListData.length > 0 && (
              <MainButton
                type="submit"
                className="align-self-end"
                isDisabled={
                  requestUpdateNomenclaturesFromEntityStatus ===
                    STATUSPAGE.LOADING ||
                  requestGetALlNomenclaturesFromEntityStatus ===
                    STATUSPAGE.LOADING
                }
                isLoading={
                  requestUpdateNomenclaturesFromEntityStatus ===
                  STATUSPAGE.LOADING
                }
              >
                Salvar
              </MainButton>
            )}
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};
